// src/routes/PermissionRoute.jsx
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { usePermissions } from "../hooks/usePermissions";
import { useLocalization } from "../contexts/LocalizationContext";
import { AlertCircle, Shield } from "lucide-react";

const PermissionRoute = ({
  children,
  requiredPermission,
  requiredPermissions,
  requireAll = false,
  fallbackPath = "/dashboard",
  showAccessDenied = true,
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } =
    usePermissions();
  const { t } = useLocalization();
  const location = useLocation();

  // Check permissions
  let hasAccess = false;

  if (requiredPermission) {
    // Single permission check
    hasAccess = hasPermission(requiredPermission);
  } else if (requiredPermissions && Array.isArray(requiredPermissions)) {
    // Multiple permissions check
    if (requireAll) {
      hasAccess = hasAllPermissions(requiredPermissions);
    } else {
      hasAccess = hasAnyPermission(requiredPermissions);
    }
  } else {
    // No permission requirements, allow access
    hasAccess = true;
  }

  if (!hasAccess) {
    if (showAccessDenied) {
      return (
        <AccessDeniedPage
          requiredPermission={requiredPermission}
          requiredPermissions={requiredPermissions}
        />
      );
    } else {
      return <Navigate to={fallbackPath} state={{ from: location }} replace />;
    }
  }

  return children;
};

// Access Denied component
const AccessDeniedPage = ({ requiredPermission, requiredPermissions }) => {
  const { t } = useLocalization();

  const permissionsList =
    requiredPermissions || (requiredPermission ? [requiredPermission] : []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        padding: "2rem",
        textAlign: "center",
        background: "white",
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        margin: "2rem auto",
        maxWidth: "600px",
      }}
    >
      <div
        style={{
          width: "80px",
          height: "80px",
          background: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "1.5rem",
        }}
      >
        <Shield size={40} style={{ color: "#ef4444" }} />
      </div>

      <h1
        style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          color: "#1f2937",
          margin: "0 0 0.5rem 0",
        }}
      >
        {t("errors.accessDenied", "Access Denied")}
      </h1>

      <p
        style={{
          color: "#6b7280",
          fontSize: "1rem",
          margin: "0 0 1.5rem 0",
          lineHeight: "1.5",
        }}
      >
        {t(
          "errors.insufficientPermissions",
          "You don't have sufficient permissions to access this page."
        )}
      </p>

      {permissionsList.length > 0 && (
        <div
          style={{
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "1rem",
            marginBottom: "1.5rem",
            width: "100%",
            maxWidth: "400px",
          }}
        >
          <h3
            style={{
              fontSize: "0.875rem",
              fontWeight: "600",
              color: "#374151",
              margin: "0 0 0.5rem 0",
            }}
          >
            {t("errors.requiredPermissions", "Required Permissions:")}
          </h3>
          <ul
            style={{
              listStyle: "none",
              padding: "0",
              margin: "0",
            }}
          >
            {permissionsList.map((permission, index) => (
              <li
                key={index}
                style={{
                  fontSize: "0.875rem",
                  color: "#6b7280",
                  padding: "0.25rem 0",
                  borderBottom:
                    index < permissionsList.length - 1
                      ? "1px solid #e5e7eb"
                      : "none",
                }}
              >
                •{" "}
                {permission
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase())}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: "1rem",
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <button
          onClick={() => window.history.back()}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.75rem 1rem",
            background: "white",
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            color: "#374151",
            textDecoration: "none",
            fontWeight: "500",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "#f9fafb";
            e.target.style.borderColor = "#9ca3af";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "white";
            e.target.style.borderColor = "#d1d5db";
          }}
        >
          {t("common.goBack", "Go Back")}
        </button>

        <button
          onClick={() => (window.location.href = "/dashboard")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.75rem 1rem",
            background: "linear-gradient(135deg, #7F5CFF 0%, #1F3C88 100%)",
            border: "none",
            borderRadius: "8px",
            color: "white",
            textDecoration: "none",
            fontWeight: "500",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = "translateY(-1px)";
            e.target.style.boxShadow =
              "0 10px 20px -5px rgba(102, 126, 234, 0.4)";
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "none";
          }}
        >
          {t("nav.dashboard", "Dashboard")}
        </button>
      </div>

      <p
        style={{
          fontSize: "0.875rem",
          color: "#9ca3af",
          margin: "1.5rem 0 0 0",
        }}
      >
        {t(
          "errors.contactAdminForAccess",
          "Contact your administrator if you believe you should have access to this page."
        )}
      </p>
    </div>
  );
};

export default PermissionRoute;
